import React, {useContext, useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {Formik} from 'formik'
import {Avatar, Button, Input, message, Modal, Space} from 'antd'
import {LoadingOutlined} from '@ant-design/icons'
import {BsPersonFill} from 'react-icons/bs'
import {RiSearch2Line} from 'react-icons/ri'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import LookupField from 'components/LookupField'
import UserLookupComponent from 'components/UserLookupComponent'
import Translate from 'modules/local/Translate'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {Field, FormActions, Section} from 'components/form/ModernForm'
import {renderParishLookupOption, renderSaintLookupOption} from '../functions/lookupOptionRenderers'
import {
    familyBook_addFamilyBookAdmin_Api,
    familyBook_getFamilyBookAdmins_Api,
    familyBook_removeFamilyBookAdmin_Api,
    familyBook_updateFamilyBook_Api,
} from 'apis/familyBook'
import FamilyBookTransferLogSection from './FamilyBookTransferLogSection'
import {InfoBanner} from './MomentRow'

// Quản trị viên tạm thời - gán quyền EDIT sổ cho 1 user NGOÀI cây gia phả (không cần là thân nhân
// thật, khác hẳn "Thêm Thân Nhân" - đó là 1 người TRONG cây), dùng khi cần nhờ người hỗ trợ nhập
// liệu 1 thời gian rồi gỡ quyền. Tách khỏi Formik chính (đây là các hành động tức thời - thêm/gỡ 1
// người là xong ngay, không phải điền form rồi bấm Lưu chung) - cùng tinh thần "LinkedBooksTab"
// trong FamilyLinkQrModal.js. Tìm user CHÍNH XÁC qua UserLookupComponent (email/mã định danh) -
// đúng convention đã dùng cho member_user_id ở AddRelativeForm, không tự chế lookup theo tên.
const FamilyBookAdminsSection = ({familyBookId, t}) => {
    const [refreshCount, setRefreshCount] = useState(0)

    const {response, isLoading} = useAsync({
        apiInfo: familyBook_getFamilyBookAdmins_Api,
        query: {id: familyBookId},
        deps: [familyBookId, refreshCount],
    })
    const admins = _.get(response, 'data.data', _.get(response, 'data', []))

    const {handleAsyncAction: doAdd, loading: adding} = useAsyncAction({
        apiInfo: familyBook_addFamilyBookAdmin_Api,
        onSuccess: () => {
            message.success(t('book admin added'))
            setRefreshCount((c) => c + 1)
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })
    const {handleAsyncAction: doRemove} = useAsyncAction({
        apiInfo: familyBook_removeFamilyBookAdmin_Api,
        onSuccess: () => {
            message.success(t('book admin removed'))
            setRefreshCount((c) => c + 1)
        },
        onError: () => message.error(t('error occurred')),
    })

    return (
        <Section title={t('temporary book admins')} subtitle={t('temporary book admins hint')}>
            <div className="flex flex-col gap-2">
                <Translate>
                    {(tt) => (
                        <UserLookupComponent
                            handleApply={(userInfo) => {
                                if (userInfo?.id) doAdd({user_id: userInfo.id}, {id: familyBookId})
                            }}>
                            <Button
                                size="large"
                                type="primary"
                                loading={adding}
                                icon={<RiSearch2Line size={14}/>}
                                className="w-full flex flex-center gap-1.5">
                                {tt('add temporary admin')}
                            </Button>
                        </UserLookupComponent>
                    )}
                </Translate>

                {isLoading && !admins.length && (
                    <div className="flex items-center justify-center py-3"><LoadingOutlined className="text-family-500"/></div>
                )}
                {!isLoading && !admins.length && (
                    <span className="text-xs text-color-300 italic">{t('no temporary admins yet')}</span>
                )}
                <div className="flex flex-col gap-2">
                    {admins.map((a) => (
                        <div key={a.id} className="p-2 flex items-center gap-2 rounded-lg bg-color-50 border border-color-100">
                            <Avatar
                                size={32}
                                src={getAvatar(a.user)}
                                icon={<BsPersonFill size={14} className="text-color-500"/>}
                                className="flex-shrink-0 flex flex-center background-200"
                            />
                            <div className="flex-1 min-w-0 text-sm font-semibold text-color-000 truncate">{getTitle(a.user)}</div>
                            <button
                                type="button"
                                onClick={() => doRemove({}, {id: a.id})}
                                className="px-2 py-1 rounded text-2xs font-semibold text-rose-600 hover:bg-rose-50 cursor-pointer flex-shrink-0">
                                {t('remove')}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </Section>
    )
}

// Người đỡ đầu 1 bên (bên trai/bên gái) của Hôn Phối - tái dùng NGUYÊN khối "Participant" của
// SacramentEditForm (components/MemberDetailModal.js, type="matrimony") thay vì viết lại: Input +
// UserLookupComponent tra chính xác theo tài khoản, LookupField tên thánh chỉ hiện khi CHƯA liên
// kết tài khoản (có tài khoản thì tên thánh suy thẳng từ hồ sơ họ). `fieldPrefix` ('groom'/'bride')
// quyết định field nào trong `values` được đọc/ghi - dùng CHUNG 1 component cho cả 2 bên thay vì
// lặp JSX 2 lần.
const MatrimonyParticipantField = ({label, values, setValues, fieldPrefix, t}) => {
    const nameKey = `${fieldPrefix}_participant_name`
    const userIdKey = `${fieldPrefix}_participant_user_id`
    const saintKey = `${fieldPrefix}_participant_patron_saint`
    const saintNameKey = `${fieldPrefix}_participant_patron_saint_name`
    return (
        <div className="flex flex-col gap-2">
            <span className="text-2xs font-bold uppercase tracking-widest text-color-300">{label}</span>
            <Space.Compact style={{display: 'flex', width: '100%'}}>
                <Input
                    size="large"
                    value={values[nameKey]}
                    onChange={(e) => setValues({...values, [nameKey]: e.target.value, [userIdKey]: null})}
                    allowClear
                    placeholder={t('participant placeholder')}
                    className="flex-1"
                />
                <UserLookupComponent
                    handleApply={(userInfo) => {
                        if (userInfo?.id) setValues({...values, [nameKey]: userInfo.name, [userIdKey]: userInfo.id})
                    }}>
                    <Button size="large" type="primary" icon={<RiSearch2Line size={14}/>} className="flex flex-center"/>
                </UserLookupComponent>
            </Space.Compact>
            {!values[userIdKey] && (
                <LookupField
                    title={t('choose patron saint')}
                    createIfNotExists={true}
                    defaultValue={values[saintNameKey]}
                    selectedOption={values[saintKey]}
                    apiPath="/lookup/saint/get-by-types"
                    name={`${fieldPrefix}_participant_patron_saint`}
                    placeholder={t('participant patron saint placeholder')}
                    renderOption={renderSaintLookupOption}
                    handleChange={(e) => setValues({...values, [saintKey]: e.target, [saintNameKey]: e.target.value})}
                />
            )}
        </div>
    )
}

// 1 dòng label/giá trị chỉ-xem cho khối Hôn Phối khi sacraments_locked - đọc thẳng `item.matrimony`
// (dữ liệu ĐÃ LƯU thật, không phải `values` của Formik) vì đây là hiển thị "đây là bản giáo xứ đã
// xác nhận", không phải form đang chỉnh sửa. Không hiện dòng nào rỗng để đỡ rợp mắt danh sách toàn
// "Chưa có".
const MatrimonyReadOnlyField = ({label, value, t}) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-2xs font-bold uppercase tracking-widest text-color-300">{label}</span>
        <span className="text-sm text-color-000">{value || t('not provided yet')}</span>
    </div>
)

// Sửa sổ - Formik + ModernForm, cùng convention với modal tạo mới. Không cho đổi lại gia trưởng/vợ-
// chồng ở đây (đã có luồng riêng qua Thêm Thân Nhân + head_member_id) - chỉ sửa thông tin sổ.
// Export dùng chung cho cả list (FamilyBooks.js) và trang chi tiết (FamilyBookDetail.js) - không
// tự chế 2 bản form riêng.
const EditFamilyBookModal = ({item, onClose, onSuccess, t}) => {
    // Chồng/Vợ theo GENDER (1=nam/2=nữ), KHÔNG theo vai trò head/head_spouse - gia trưởng có thể
    // là vợ hoặc chồng. Khớp hệt logic BE (FamilyBook::groomMember()/brideMember()) - 1 nguồn suy
    // duy nhất, không tự đoán khác cách BE đang match participant vào đúng dòng sacrament.
    const groomMember = [item.head_member, item.head_spouse_member].find((m) => m?.gender === 1)
    const brideMember = [item.head_member, item.head_spouse_member].find((m) => m?.gender === 2)
    // isSm - Tailwind responsive prefix (sm:/md:grid-cols-*) KHÔNG đáng tin trong dự án này:
    // tailwind.css là file build tĩnh, không rebuild lại được (xem comment đầu custom.scss phần
    // .family-theme), và các biến thể responsive của grid-cols đã bị purge khỏi bản build hiện tại
    // dù vẫn thấy vài chỗ cũ trong code còn dùng "md:grid-cols-2" (thực ra không còn tác dụng gì -
    // đã kiểm chứng không có rule CSS tương ứng). Cách CHẮC CHẮN hoạt động: đổi class qua JS bằng
    // LayoutContext.isSm - đúng pattern đã dùng ở CatholicHouseholdForm.js.
    const {isSm} = useContext(LayoutContext)

    const {handleAsyncAction: doUpdate, loading} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBook_Api,
        onSuccess: () => {
            message.success(t('family book updated'))
            onSuccess && onSuccess()
        },
        onError: () => message.error(t('error occurred')),
    })

    const initialValues = {
        title: item.title || '',
        description: item.description || '',
        parish: item.parish ? {id: item.parish.id, value: item.parish.name || item.parish_name} : null,
        eff_date: item.eff_date ? dayjs(item.eff_date).format('YYYY-MM-DD') : '',
        source_idcode: item.source_idcode || '',
        contact_name: item.contact_name || '',
        contact_phone: item.contact_phone || '',
        contact_phone2: item.contact_phone2 || '',
        contact_email: item.contact_email || '',
        contact_address: item.contact_address || '',

        // Hôn Phối - field dùng chung (ngày/giáo xứ/chủ sự/mã) giống hệt nhau ở cả 2 bên sau khi BE
        // sync (xem FamilyBook::syncMatrimonyData()), lấy bên nào có trước cũng ra cùng giá trị.
        // CHƯA từng khai hôn phối -> mặc định lấy theo Ngày Lập Sổ/Giáo Xứ của chính sổ (đa số
        // trường hợp trùng nhau - sổ lập ngay khi/gần ngày cưới, tại đúng giáo xứ đó), vẫn cho sửa
        // khác nếu hôn phối diễn ra nơi khác/ngày khác - KHÔNG gộp 2 field làm 1 vì có thể lệch
        // (sổ lập nhiều năm sau khi cưới, hoặc chuyển sang giáo xứ khác từ lúc đó).
        // Field participant tách riêng theo bên - xem MatrimonyParticipantField.
        matrimony_eff_date: (item.matrimony?.groom?.eff_date || item.matrimony?.bride?.eff_date)
            ? dayjs(item.matrimony.groom?.eff_date || item.matrimony.bride?.eff_date).format('YYYY-MM-DD')
            : (item.eff_date ? dayjs(item.eff_date).format('YYYY-MM-DD') : ''),
        matrimony_parish: (item.matrimony?.groom?.parish || item.matrimony?.bride?.parish)
            ? {
                id: (item.matrimony.groom?.parish || item.matrimony.bride?.parish).id,
                value: (item.matrimony.groom?.parish || item.matrimony.bride?.parish).name
                    || item.matrimony.groom?.parish_name || item.matrimony.bride?.parish_name,
            }
            : (item.parish ? {id: item.parish.id, value: item.parish.name || item.parish_name} : null),
        matrimony_parish_name: item.matrimony?.groom?.parish_name || item.matrimony?.bride?.parish_name || item.parish_name || '',
        matrimony_officiant_name: item.matrimony?.groom?.officiant_user?.name || item.matrimony?.groom?.officiant_name
            || item.matrimony?.bride?.officiant_user?.name || item.matrimony?.bride?.officiant_name || '',
        matrimony_officiant_user_id: item.matrimony?.groom?.officiant_user?.id || item.matrimony?.bride?.officiant_user?.id || null,
        matrimony_source_idcode: item.matrimony?.groom?.source_idcode || item.matrimony?.bride?.source_idcode || '',
        groom_participant_name: item.matrimony?.groom?.participant_user?.name || item.matrimony?.groom?.participant_name || '',
        groom_participant_user_id: item.matrimony?.groom?.participant_user?.id || null,
        groom_participant_patron_saint: item.matrimony?.groom?.participant_patron_saint
            ? {id: item.matrimony.groom.participant_patron_saint.id, value: item.matrimony.groom.participant_patron_saint.name || item.matrimony?.groom?.participant_patron_saint_name}
            : null,
        groom_participant_patron_saint_name: item.matrimony?.groom?.participant_patron_saint_name || '',
        bride_participant_name: item.matrimony?.bride?.participant_user?.name || item.matrimony?.bride?.participant_name || '',
        bride_participant_user_id: item.matrimony?.bride?.participant_user?.id || null,
        bride_participant_patron_saint: item.matrimony?.bride?.participant_patron_saint
            ? {id: item.matrimony.bride.participant_patron_saint.id, value: item.matrimony.bride.participant_patron_saint.name || item.matrimony?.bride?.participant_patron_saint_name}
            : null,
        bride_participant_patron_saint_name: item.matrimony?.bride?.participant_patron_saint_name || '',
    }

    const validate = (values) => {
        const errors = {}
        if (!values.title) errors.title = t('required field')
        return errors
    }

    return (
        <Modal
            width={420}
            className="custom-modal"
            rootClassName="family-theme"
            open={true}
            title={<div className="text-center font-bold text-color-000 uppercase tracking-wide">{t('edit family book')}</div>}
            destroyOnHidden
            footer={null}
            onCancel={onClose}>
            <Formik
                initialValues={initialValues}
                validate={validate}
                validateOnMount
                onSubmit={(values) => doUpdate({
                    title: values.title,
                    description: values.description || undefined,
                    parish_id: values.parish?.id,
                    eff_date: values.eff_date || undefined,
                    source_idcode: values.source_idcode || undefined,
                    contact_name: values.contact_name || undefined,
                    contact_phone: values.contact_phone || undefined,
                    contact_phone2: values.contact_phone2 || undefined,
                    contact_email: values.contact_email || undefined,
                    contact_address: values.contact_address || undefined,

                    // sacraments_locked - KHÔNG gửi field Hôn Phối lên nữa khi giáo xứ đã khoá (Section
                    // bên dưới cũng đổi hẳn sang hiển thị chỉ-xem, không còn input để user sửa được giá
                    // trị nữa) - BE vẫn tự chặn lại nếu có (xem hasChangedMatrimonyFields()), đây chỉ là
                    // lớp phòng thủ đầu (không gửi thứ không sửa được thay vì để BE trả lỗi).
                    ...(item.sacraments_locked ? {} : {
                        matrimony_eff_date: values.matrimony_eff_date || undefined,
                        matrimony_parish_id: values.matrimony_parish?.id || undefined,
                        matrimony_parish_name: values.matrimony_parish?.value || values.matrimony_parish_name || undefined,
                        matrimony_officiant_user_id: values.matrimony_officiant_user_id || undefined,
                        matrimony_officiant_name: values.matrimony_officiant_name || undefined,
                        matrimony_source_idcode: values.matrimony_source_idcode || undefined,
                        matrimony_groom_participant_user_id: values.groom_participant_user_id || undefined,
                        matrimony_groom_participant_name: values.groom_participant_name || undefined,
                        matrimony_groom_participant_patron_saint_id: values.groom_participant_patron_saint?.id || undefined,
                        matrimony_groom_participant_patron_saint_name: values.groom_participant_patron_saint?.value || values.groom_participant_patron_saint_name || undefined,
                        matrimony_bride_participant_user_id: values.bride_participant_user_id || undefined,
                        matrimony_bride_participant_name: values.bride_participant_name || undefined,
                        matrimony_bride_participant_patron_saint_id: values.bride_participant_patron_saint?.id || undefined,
                        matrimony_bride_participant_patron_saint_name: values.bride_participant_patron_saint?.value || values.bride_participant_patron_saint_name || undefined,
                    }),
                }, {id: item.id})}>
                {({values, touched, errors, setFieldValue, setValues, handleSubmit, isSubmitting, isValid}) => (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-2">
                        {/* Sổ đã được giáo xứ xác nhận - họ cũng sửa được các field ở form này (tên/
                            mô tả/liên hệ/ngày lập/mã sổ, xem FamilyBookController::
                            updateFamilyBookInfoAsParish()) để đối chiếu, KHÔNG chỉ riêng bí tích -
                            cần nói rõ ngay đầu form, không chỉ ở Section Hôn Phối bên dưới, vì field
                            đầu tiên user thấy (Tên Sổ) chính là 1 trong các field đó. */}
                        {item.status === 1 && (
                            <InfoBanner>{t('book info editable by parish hint')}</InfoBanner>
                        )}
                        <Section title={t('family book title')} theme="highlight">
                            <Field required error={touched.title && errors.title}>
                                <Input
                                    size="large"
                                    value={values.title}
                                    onChange={(e) => setFieldValue('title', e.target.value)}
                                    placeholder={t('family book title placeholder')}
                                />
                            </Field>
                        </Section>

                        {/* Hôn Phối - đặt NGAY SAU Tên Sổ (trước Thuộc giáo xứ/Mô tả/Thông tin sổ):
                            với sổ hôn phối, danh tính cặp đôi là thông tin định danh sổ quan trọng
                            hơn giáo xứ/mô tả, nên ưu tiên hiển thị trước - đúng góp ý thiết kế "tên
                            sổ là tên của cặp đôi, đưa hôn phối lên trên". Gộp vào màn Sửa Sổ thay vì
                            tách riêng từng thành viên (trước đây phải mở từng thành viên qua
                            MemberDetailModal/SacramentEditForm mới sửa được). Chỉ hiện khi sổ đã có
                            ít nhất 1 trong 2 vợ chồng (mới lập sổ xong luôn có, nhưng phòng ca dữ
                            liệu cũ/sổ do org tạo hộ chưa có ai). */}
                        {(groomMember || brideMember) && (
                            <Section title={t('family_book_matrimony_title')} subtitle={t('optional')}>
                                {/* Trước là 1 dòng chữ xám nghiêng nhỏ, dễ bị lướt qua - LÀM RÕ lại
                                    thành khối nổi bật (nền family, chữ đậm, có nhãn Chồng/Vợ rõ
                                    ràng) vì đây là thông tin định danh CẶP ĐÔI của cả khối Hôn Phối
                                    (khớp đúng tên sổ ở trên) - cần thấy ngay ai là "bên trai"/"bên
                                    gái" trước khi đọc xuống Người Làm Chứng bên dưới. Nhãn đặt
                                    TRÊN 1 dòng riêng (không cùng dòng với tên) - modal hẹp (420px),
                                    tên dài (họ tên đầy đủ tiếng Việt) dễ bị cắt "..." nếu nhãn+tên
                                    chung 1 hàng, xem tên đầy đủ quan trọng hơn cô đọng theo chiều
                                    dọc ở đây. */}
                                <div className="flex flex-col gap-2 p-2.5 rounded-lg bg-family-50 border border-family-200">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-2xs font-bold uppercase tracking-wide text-family-600">{t('husband')}</span>
                                        <span className="text-sm font-bold text-color-000">
                                            {groomMember?.name || t('not determined yet')}
                                        </span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-2xs font-bold uppercase tracking-wide text-family-600">{t('wife')}</span>
                                        <span className="text-sm font-bold text-color-000">
                                            {brideMember?.name || t('not determined yet')}
                                        </span>
                                    </div>
                                </div>

                                {/* sacraments_locked - giáo xứ đã đối chiếu bí tích với sổ vật lý lúc
                                    xác nhận liên kết rồi khoá lại (xem FamilyBookController::
                                    changeFamilyBookParishLinkStatus()) - đổi HẲN sang hiển thị chỉ-xem
                                    (không còn input nào), KHÔNG chỉ disable từng field lẻ tẻ (nhiều
                                    field/nhiều loại control ở đây - Input/LookupField/UserLookupComponent
                                    - disable riêng từng cái dễ sót, đổi nguyên khối chắc chắn hơn). */}
                                {item.sacraments_locked ? (
                                    <>
                                        <InfoBanner>{t('sacraments locked by parish hint')}</InfoBanner>
                                        <MatrimonyReadOnlyField label={t('wedding date')}
                                            value={item.matrimony?.groom?.eff_date || item.matrimony?.bride?.eff_date
                                                ? dayjs(item.matrimony.groom?.eff_date || item.matrimony.bride?.eff_date).format('DD/MM/YYYY') : null} t={t}/>
                                        <MatrimonyReadOnlyField label={t('parish')}
                                            value={item.matrimony?.groom?.parish_name || item.matrimony?.bride?.parish_name} t={t}/>
                                        <MatrimonyReadOnlyField label={t('officiant')}
                                            value={item.matrimony?.groom?.officiant_user?.name || item.matrimony?.groom?.officiant_name
                                                || item.matrimony?.bride?.officiant_user?.name || item.matrimony?.bride?.officiant_name} t={t}/>
                                        {groomMember && (
                                            <MatrimonyReadOnlyField label={t('witness_groom_side')}
                                                value={item.matrimony?.groom?.participant_user?.name || item.matrimony?.groom?.participant_name} t={t}/>
                                        )}
                                        {brideMember && (
                                            <MatrimonyReadOnlyField label={t('witness_bride_side')}
                                                value={item.matrimony?.bride?.participant_user?.name || item.matrimony?.bride?.participant_name} t={t}/>
                                        )}
                                        <MatrimonyReadOnlyField label={t('matrimony_source_idcode_label')}
                                            value={item.matrimony?.groom?.source_idcode || item.matrimony?.bride?.source_idcode} t={t}/>
                                    </>
                                ) : (
                                    <>
                                        <Field label={t('wedding date')}>
                                            <Input
                                                type="date"
                                                size="large"
                                                className="w-full"
                                                value={values.matrimony_eff_date}
                                                onChange={(e) => setFieldValue('matrimony_eff_date', e.target.value)}
                                            />
                                        </Field>

                                        {/* Giáo xứ/Chủ sự - field DÙNG CHUNG cho cả 2 bên (BE tự đồng bộ
                                            giống hệt lên cả 2 dòng matrimony, xem FamilyBook::syncMatrimonyData()),
                                            trước đây chỉ có ở modal sửa từng thành viên (SacramentEditForm)
                                            mà bị thiếu khi gộp Section này - bổ sung lại cho đủ, cùng pattern
                                            LookupField/UserLookupComponent đã dùng ở đó. */}
                                        <Field label={t('parish')}>
                                            <LookupField
                                                title={t('choose parish')}
                                                createIfNotExists={true}
                                                defaultValue={values.matrimony_parish_name}
                                                selectedOption={values.matrimony_parish}
                                                apiPath="/lookup/parish/get-by-types"
                                                name="matrimony_parish"
                                                renderOption={renderParishLookupOption}
                                                handleChange={(e) => setValues({...values, matrimony_parish: e.target, matrimony_parish_name: e.target.value})}
                                            />
                                        </Field>

                                        <Field label={t('officiant')}>
                                            <Space.Compact style={{display: 'flex', width: '100%'}}>
                                                <Input
                                                    size="large"
                                                    value={values.matrimony_officiant_name}
                                                    onChange={(e) => setValues({...values, matrimony_officiant_name: e.target.value, matrimony_officiant_user_id: null})}
                                                    allowClear
                                                    placeholder={t('officiant placeholder')}
                                                    className="flex-1"
                                                />
                                                <UserLookupComponent
                                                    handleApply={(userInfo) => {
                                                        if (userInfo?.id) setValues({...values, matrimony_officiant_name: userInfo.name, matrimony_officiant_user_id: userInfo.id})
                                                    }}>
                                                    <Button size="large" type="primary" icon={<RiSearch2Line size={14}/>} className="flex flex-center"/>
                                                </UserLookupComponent>
                                            </Space.Compact>
                                        </Field>

                                        {/* Nhãn "làm chứng" (KHÔNG phải "đỡ đầu") - đúng thuật ngữ Giáo Luật
                                            cho vai trò 2 người chứng hôn phối, khác vai trò đỡ đầu của rửa
                                            tội/thêm sức. */}
                                        {groomMember && (
                                            <MatrimonyParticipantField
                                                label={t('witness_groom_side')}
                                                values={values} setValues={setValues}
                                                fieldPrefix="groom" t={t}
                                            />
                                        )}
                                        {brideMember && (
                                            <MatrimonyParticipantField
                                                label={t('witness_bride_side')}
                                                values={values} setValues={setValues}
                                                fieldPrefix="bride" t={t}
                                            />
                                        )}

                                        <Field label={t('matrimony_source_idcode_label')}>
                                            <Input
                                                size="large"
                                                className="w-full"
                                                value={values.matrimony_source_idcode}
                                                onChange={(e) => setFieldValue('matrimony_source_idcode', e.target.value)}
                                            />
                                        </Field>
                                    </>
                                )}
                            </Section>
                        )}

                        <Section title={t('description')} subtitle={t('optional')}>
                            <Input.TextArea
                                rows={3}
                                value={values.description}
                                onChange={(e) => setFieldValue('description', e.target.value)}
                            />
                        </Section>

                        <Section title={t('family book info')} subtitle={t('optional')}>
                            {/* grid-cols-2 CHỈ ở màn hình đủ rộng - stack 1 cột trên mobile (isSm),
                                tránh input type="date" (có sub-control ngày/tháng/năm/icon lịch,
                                rộng hơn input thường) bị bóp quá hẹp gây chồng đè - xem quy tắc
                                "Grid 2 cột trong form" ở docs/ui/form-layout.md. */}
                            <div className={classNames('grid gap-2', isSm ? 'grid-cols-1' : 'grid-cols-2')}>
                                <Field label={t('family book eff date')}>
                                    <Input
                                        type="date"
                                        size="large"
                                        className="w-full"
                                        value={values.eff_date}
                                        onChange={(e) => setFieldValue('eff_date', e.target.value)}
                                    />
                                </Field>
                                <Field label={t('family book source code')}>
                                    <Input
                                        size="large"
                                        className="w-full"
                                        value={values.source_idcode}
                                        onChange={(e) => setFieldValue('source_idcode', e.target.value)}
                                    />
                                </Field>
                            </div>
                        </Section>

                        {/* Thuộc giáo xứ - đặt SAU Thông tin sổ (thay vì ngay đầu form như trước):
                            sổ có thể đổi giáo xứ nhiều lần theo thời gian (mất sổ, làm lại ở giáo xứ
                            khác...) nên giá trị NGAY TẠI ĐÂY chỉ còn là giáo xứ HIỆN TẠI (master) -
                            LookupField này vẫn đổi thẳng field đó như cũ (submit qua parish_id bình
                            thường, BE tự động ghi 1 dòng lịch sử mới khi đổi - xem
                            FamilyBook::recordTransfer()). FamilyBookTransferLogSection bên
                            dưới hiện DANH SÁCH các lần chuyển trước đó theo "từ ngày - đến ngày",
                            có thể thêm/sửa tay để bổ sung các lần chuyển xảy ra trước khi có tính
                            năng này. */}
                        <Section title={t('belong to parish')} subtitle={t('parish link needs confirmation note')}>
                            <LookupField
                                name="family_book_parish_edit"
                                apiPath="/lookup/parish/get-by-types"
                                placeholder={t('choose parish')}
                                defaultValue={values.parish?.value}
                                renderOption={renderParishLookupOption}
                                handleChange={(e) => setFieldValue('parish', e.target)}
                            />
                            {/* Chỉ nhắc khi thật sự ĐỔI giáo xứ (khác giáo trị đang lưu) - đúng đằng
                                nào status cũng chỉ reset -1 khi parish_id đổi (xem updateFamilyBook()
                                BE), giữ nguyên giáo xứ cũ thì không có gì thay đổi để cảnh báo. */}
                            {values.parish?.id && values.parish.id !== item.parish?.id && (
                                <InfoBanner>{t('parish link pending sacraments lock note')}</InfoBanner>
                            )}
                            <FamilyBookTransferLogSection familyBookId={item.id} canEdit={item.edit} t={t}/>
                        </Section>

                        {/* Chỉ nhóm quản lý sổ (chủ sổ/org admin/quản trị viên tạm thời - xem
                            canManageFamilyBookContact()) mới thấy Section này - thân nhân bình
                            thường tuy sửa được các field khác trong form nhưng KHÔNG thấy/sửa được
                            "Thông tin liên hệ" (nhạy cảm hơn). item.can_manage_contact khác undefined
                            với false có chủ đích - undefined (item đến từ 1 API list cũ chưa kịp có
                            field này) mặc định vẫn HIỆN để không vô tình ẩn nhầm với đúng chủ sổ, chỉ
                            ẩn khi backend xác nhận rõ ràng false. Thu gọn mặc định (collapsible) vì
                            ít khi cần sửa, tránh form dài ngay từ đầu. */}
                        {item.can_manage_contact !== false && (
                            <Section title={t('contact info')} subtitle={t('optional')} collapsible defaultCollapsed>
                                <Field label={t('contact name')}>
                                    <Input value={values.contact_name} onChange={(e) => setFieldValue('contact_name', e.target.value)}/>
                                </Field>
                                <div className={classNames('grid gap-2', isSm ? 'grid-cols-1' : 'grid-cols-2')}>
                                    <Field label={t('contact phone')}>
                                        <Input value={values.contact_phone} onChange={(e) => setFieldValue('contact_phone', e.target.value)}/>
                                    </Field>
                                    <Field label={t('contact phone 2')}>
                                        <Input value={values.contact_phone2} onChange={(e) => setFieldValue('contact_phone2', e.target.value)}/>
                                    </Field>
                                </div>
                                <Field label={t('contact email')}>
                                    <Input value={values.contact_email} onChange={(e) => setFieldValue('contact_email', e.target.value)}/>
                                </Field>
                                <Field label={t('address')}>
                                    <Input value={values.contact_address} onChange={(e) => setFieldValue('contact_address', e.target.value)}/>
                                </Field>
                            </Section>
                        )}

                        <FormActions
                            onCancel={onClose}
                            submitText="save"
                            isSubmitting={isSubmitting || loading}
                            isValid={isValid}
                        />
                    </form>
                )}
            </Formik>

            <div className="mt-3 pt-3 border-t border-color-50">
                <FamilyBookAdminsSection familyBookId={item.id} t={t}/>
            </div>
        </Modal>
    )
}

export default EditFamilyBookModal
